import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const athleteImages = [
  {
    id: 1,
    image: 'https://customer-assets.emergentagent.com/job_nutrasupply/artifacts/i6ayk2y8_IMG_2439.jpeg',
    name: 'Puskar Gurung',
    title: 'Professional Coach',
    quote: 'NutraFuel has been my go-to for all supplements. 100% authentic every time.',
    imageStyle: { objectPosition: 'center 40%' },
  },
  {
    id: 2,
    image: 'https://customer-assets.emergentagent.com/job_nutrasupply/artifacts/leyy74s0_IMG_2445.jpeg',
    name: 'James Magar',
    title: 'Fitness Influencer',
    quote: 'Finally found a trustworthy supplement store. No more worrying about fake products.',
    imageStyle: { objectPosition: 'top' },
  },
  {
    id: 3,
    image: 'https://customer-assets.emergentagent.com/job_nutrasupply/artifacts/xrikh08w_IMG_2447.jpeg',
    name: 'Romika Neupane',
    title: "Nepal's Strongest Female Deadlifter",
    quote: 'The team at NutraFuel understands athletes. They know what we need.',
    imageStyle: { objectPosition: 'center 35%' },
  },
  {
    id: 4,
    image: 'https://customer-assets.emergentagent.com/job_nutrasupply/artifacts/nocsnx4h_IMG_2453.jpeg',
    name: 'Susanta Lisham',
    title: 'Fitness Model & Influencer',
    quote: 'Finally found supplements I can trust completely.',
    imageStyle: { objectPosition: 'center 45%' },
  },
];

export const AthleteTrustSection = () => {
  return (
    <section className="py-24 relative overflow-hidden section-premium-light texture-grain">
      {/* Soft Radial Glow Effects */}
      <div className="absolute top-0 right-0 w-[450px] h-[450px] bg-primary/5 rounded-full blur-[150px]" />
      <div className="absolute bottom-0 left-0 w-[350px] h-[350px] bg-gold/5 rounded-full blur-[120px]" />
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <span className="inline-flex items-center gap-2 text-gold font-semibold text-sm uppercase tracking-wider mb-4">
              <span className="w-8 h-px bg-gold/50" />
              ATHLETE COMMUNITY
            </span>
            <h2 className="section-title text-foreground">Trusted by <span className="text-primary">Athletes</span></h2>
          </div>
          <Button variant="outline" className="border-primary/30 hover:bg-primary hover:text-white transition-all hover:-translate-y-0.5" asChild>
            <Link to="/athletes">
              View All Athletes
              <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
            </Link>
          </Button>
        </div>

        {/* Athletes Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {athleteImages.map((athlete, index) => (
            <Card
              key={athlete.id}
              className="group overflow-hidden bg-white/80 backdrop-blur-sm border border-border/50 hover:border-primary/30 card-hover animate-fade-up"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="relative h-60 overflow-hidden">
                <img
                  src={athlete.image}
                  alt={athlete.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  style={athlete.imageStyle || { objectPosition: 'top' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h4 className="text-lg font-bold text-white">{athlete.name}</h4>
                  <p className="text-sm text-gold">{athlete.title}</p>
                </div>
              </div>
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground italic">&quot;{athlete.quote}&quot;</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Community Stats */}
        <div className="mt-14 relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#f8f5f0] via-[#f5f0eb] to-[#f8f5f0] border border-gold/20 p-8">
          {/* Beautiful Background Elements */}
          <div className="absolute top-0 left-0 w-32 h-32 bg-primary/10 rounded-full blur-[60px]" />
          <div className="absolute bottom-0 right-0 w-40 h-40 bg-gold/10 rounded-full blur-[80px]" />
          <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-primary/5 rounded-full blur-[50px]" />
          <div className="absolute top-1/3 right-1/4 w-20 h-20 bg-gold/5 rounded-full blur-[40px]" />
          
          {/* Decorative Shapes */}
          <div className="absolute top-4 left-8 w-3 h-3 bg-primary/20 rounded-full" />
          <div className="absolute bottom-6 right-12 w-2 h-2 bg-gold/30 rounded-full" />
          <div className="absolute top-1/2 right-8 w-4 h-4 border border-primary/20 rounded-full" />
          <div className="absolute bottom-8 left-1/4 w-3 h-3 border border-gold/20 rounded-full" />
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 relative z-10">
            {[
              { value: '50+', label: 'Partner Athletes' },
              { value: '20+', label: 'Gym Collaborations' },
              { value: '15+', label: 'Competition Sponsors' },
              { value: '100+', label: 'Events Hosted' },
            ].map((stat, index) => (
              <div key={stat.label} className="text-center animate-fade-up" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="text-4xl md:text-5xl font-display font-bold text-primary mb-2">{stat.value}</div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Section Separator */}
      <div className="absolute bottom-0 left-0 right-0 section-separator" />
    </section>
  );
};
